<template>
  <b-form
    v-if="user"
    enctype="multipart/form-data"
    class="profile-form form"
    @submit.prevent="onSubmit"
  >
    <div class="form__section">
      <h3 class="page-subtitle">Profile Photo</h3>
      <b-row>
        <b-col cols="6">
          <image-cropper
            ref="avatarCropper"
            v-model="showAvatarUpload"
            :aspect-ratio="1 / 1"
            :initial-aspect-ratio="1 / 1"
            @change="uploadAvatar"
            @close="showAvatarUpload = false"
          />
          <div
            :class="[
              'upload-container mb-4',
              {
                'border-0': userAvatar
              }
            ]"
            v-cloak
            @drop.prevent="onAvatarChange"
            @dragover.prevent
          >
            <b-img
              v-if="userAvatar"
              :src="userAvatar"
              alt="Avatar"
              fluid-grow
              center
              rounded
              class="user-avatar"
            ></b-img>

            <b-img
              v-else
              :src="require('@/assets/images/img-placeholder.svg')"
              alt="Photos"
              center
              class="user-placeholder"
            ></b-img>
          </div>
        </b-col>
        <b-col
          cols="6"
          class="d-flex justify-content-center align-items-center flex-column"
        >
          <b-button
            block
            variant="outline-primary"
            class="btn--upload upload-container-btn mb-4"
            :disabled="form.$busy"
            @click="showAvatarUpload = true"
            >Upload photo</b-button
          >
          <div class="pb-5 upload-container-text">Or drag them in</div>
        </b-col>
      </b-row>
      <error-message :error="form.$getError('avatar')"></error-message>
    </div>
    <div class="form__section">
      <h3 class="page-subtitle">Personal Details</h3>
      <b-row>
        <b-col>
          <b-form-group>
            <custom-input
              v-model="form.full_name"
              type="text"
              required
              autofocus
              :disabled="form.$busy"
              :is-invalid="form.$getError('full_name')"
              placeholder="Full Name"
            ></custom-input>
            <error-message :error="form.$getError('full_name')"></error-message>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group>
            <custom-input
              v-model="form.birth_date"
              type="text"
              placeholder="Date of Birth"
              readonly
              required
              :disabled="form.$busy"
              :is-invalid="form.$getError('birth_date')"
              @click="showDatepicker(true)"
              @focus="showDatepicker(true)"
              @blur="showDatepicker(false)"
            >
            </custom-input>
            <date-picker
              ref="datepicker"
              v-model="datepickerValue"
              :is-modal="true"
              :is-auto-closeable="false"
              :change-month-function="true"
              :change-year-function="true"
              :new-current-date="datepickerCurrentDate"
              :limits="datepickerBirthLimit"
              :date-format="datepickerFormat"
              @dayClicked="onSelectedDate"
            ></date-picker>
            <error-message
              :error="form.$getError('birth_date')"
            ></error-message>
          </b-form-group>
        </b-col>
      </b-row>
    </div>
    <div class="form__section">
      <h3 class="page-subtitle">Address</h3>
      <b-row>
        <b-col>
          <b-form-group>
            <custom-input
              v-model="form.address_1"
              type="text"
              required
              :disabled="form.$busy"
              :is-invalid="form.$getError('address_1')"
              placeholder="Address Line 1"
            ></custom-input>
            <error-message :error="form.$getError('address_1')"></error-message>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group>
            <custom-input
              v-model="form.city"
              type="text"
              required
              :disabled="form.$busy"
              :is-invalid="form.$getError('city')"
              placeholder="City or Suburb"
            ></custom-input>
            <error-message :error="form.$getError('city')"></error-message>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group>
            <custom-input
              v-model="form.postal_code"
              type="number"
              required
              :disabled="form.$busy"
              :is-invalid="form.$getError('postal_code')"
              placeholder="Postcode"
            ></custom-input>
            <error-message
              :error="form.$getError('postal_code')"
            ></error-message>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group>
            <custom-input
              v-model="form.state"
              type="text"
              required
              :disabled="form.$busy"
              :is-invalid="form.$getError('state')"
              placeholder="State"
            ></custom-input>
            <error-message :error="form.$getError('state')"></error-message>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group>
            <custom-input
              v-model="form.country"
              type="text"
              required
              :disabled="form.$busy"
              :is-invalid="form.$getError('country')"
              placeholder="Country"
            ></custom-input>
            <error-message :error="form.$getError('country')"></error-message>
          </b-form-group>
        </b-col>
      </b-row>
    </div>
    <div class="form__section">
      <h3 class="page-subtitle">ID Verification</h3>

      <b-alert
        :show="showIdAlert"
        :variant="idAlertState"
        class="account-alert alert__padding alert__border d-flex flex-row align-items-center"
      >
        <div>
          <b-img
            :src="idAlertIcon"
            :width="idAlertIconSize"
            alt="Alert"
            class="mr-3"
          ></b-img>
        </div>
        <div>
          <p>
            <b>{{ idAlertTitle }}</b>
          </p>
          <p>{{ idAlertContent }}</p>
        </div>
      </b-alert>

      <div class="form__section__header">
        <h4 class="header__title">Your ID</h4>
        <h5 class="header__content">
          Provide a photo of the front of your drivers license
        </h5>
      </div>
      <b-row>
        <b-col cols="6">
          <image-cropper
            ref="photoFrontCropper"
            v-model="showPhotoFrontUpload"
            @change="uploadPhotoFront"
            @close="showPhotoFrontUpload = false"
          />
          <div
            class="upload-container upload-container--sm mb-4"
            v-cloak
            @drop.prevent="onPhotoFrontChange"
            @dragover.prevent
          >
            <b-img
              v-if="userPhotoFront"
              :src="userPhotoFront"
              alt="Avatar"
              fluid
              center
              rounded
              class="user-id"
            ></b-img>

            <b-img
              v-else
              :src="require('@/assets/images/id.svg')"
              alt="Photos"
              width="180"
              center
              class="user-placeholder"
            ></b-img>
          </div>
        </b-col>
        <b-col
          cols="6"
          class="d-flex justify-content-center align-items-center flex-column"
        >
          <b-button
            block
            variant="outline-primary"
            class="btn--upload upload-container-btn mb-4"
            :disabled="form.$busy"
            @click="showPhotoFrontUpload = true"
            >Upload photo</b-button
          >
          <p class="pb-5 upload-container-text">Or drag them in</p>
        </b-col>
      </b-row>
      <error-message :error="form.$getError('photo_id_front')"></error-message>

      <div class="divider mt-5 mb-4"></div>

      <div class="form__section__header">
        <h4 class="header__title">Headshot</h4>
        <h5 class="header__content">
          Add a selfie to verify that your face matches your license
        </h5>
      </div>
      <b-row>
        <b-col cols="6">
          <image-cropper
            ref="headshotCropper"
            v-model="showHeadshotUpload"
            @change="uploadHeadshot"
            @close="showHeadshotUpload = false"
          />
          <div
            class="upload-container upload-container--sm mb-4"
            v-cloak
            @drop.prevent="onHeadshotChange"
            @dragover.prevent
          >
            <b-img
              v-if="userHeadshot"
              :src="userHeadshot"
              alt="Avatar"
              fluid
              center
              rounded
              class="user-id"
            ></b-img>

            <b-img
              v-else
              :src="require('@/assets/images/headshot.svg')"
              alt="Photos"
              width="180"
              center
              class="user-placeholder"
            ></b-img>
          </div>
        </b-col>
        <b-col
          cols="6"
          class="d-flex justify-content-center align-items-center flex-column"
        >
          <b-button
            block
            variant="outline-primary"
            class="btn--upload upload-container-btn mb-4"
            :disabled="form.$busy"
            @click="showHeadshotUpload = true"
            >Upload photo</b-button
          >
          <p class="pb-5 upload-container-text">Or drag them in</p>
        </b-col>
      </b-row>
    </div>
    <div class="form__section form__section--mt">
      <b-row>
        <b-col>
          <b-button
            block
            variant="outline-secondary"
            :disabled="form.$busy"
            @click="resetForm(true)"
            >Discard</b-button
          >
        </b-col>
        <b-col>
          <b-button
            type="submit"
            block
            variant="primary"
            :disabled="form.$busy"
          >
            <b-spinner small v-if="form.$busy"></b-spinner>
            Save</b-button
          >
        </b-col>
      </b-row>
    </div>
  </b-form>
</template>
<script>
/**
 * ==================================================================================
 * Profile Form
 * ==================================================================================
 **/

import { mapState, mapGetters, mapActions } from 'vuex'
import Form from '@/utils/form'
import Datepicker from '@/utils/mixins/datepicker'
import ErrorMessage from '@/components/inputs/ErrorMessage.vue'
import ImageCropper from '@/components/modals/ImageCropper'
import CustomInput from '@/components/inputs/CustomInput'

export default {
  components: {
    ImageCropper,
    CustomInput,
    ErrorMessage
  },
  mixins: [Datepicker],
  computed: {
    ...mapState({
      user: state => state.auth.user
    }),
    ...mapGetters({
      userAvatar: 'auth/getUserAvatar',
      userPhotoFront: 'auth/getUserPhotoFront',
      userHeadshot: 'auth/getUserHeadshot',
      hasVerificationIds: 'auth/hasVerificationIds',
      isIdPending: 'auth/isIdPending',
      isIdDeclined: 'auth/isIdDeclined',
      isIdVerified: 'auth/isIdVerified'
    }),

    idAlertIcon() {
      if (this.isIdDeclined) return require('@/assets/images/alert.svg')
      return require('@/assets/images/alert-info.svg')
    },

    idAlertIconSize() {
      return this.isIdDeclined ? 55 : 32
    },

    idAlertState() {
      if (this.isIdPending) return 'warning'
      if (this.isIdDeclined) return 'danger'
      return 'warning'
    },

    idAlertTitle() {
      if (this.isIdPending) return 'Pending approval'
      if (this.isIdDeclined) return 'Your IDs have been declined'
      return 'Upload your IDs'
    },

    idAlertContent() {
      if (this.isIdPending)
        return 'Please wait while our admin team checks your verification IDs for approval'
      if (this.isIdDeclined)
        return `Reason: ${this.user.photo_id.decline_reason}`
      return 'To post or rent tools, please upload your verification IDs and await approval'
    },

    showIdAlert() {
      return !this.isIdVerified
    }
  },
  watch: {
    user(newVal) {
      this.resetForm()
    }
  },

  data: function() {
    return {
      showAvatarUpload: false,
      showPhotoFrontUpload: false,
      showHeadshotUpload: false,
      form: new Form({
        full_name: null,
        birth_date: null,
        address_1: null,
        city: null,
        postal_code: null,
        state: null,
        country: null
      })
    }
  },
  methods: {
    ...mapActions({
      updateUser: 'auth/updateUser',
      changeAvatar: 'auth/changeAvatar',
      changePhoto: 'auth/changePhoto',
      addAlert: 'alert/addAlert'
    }),

    onSelectedDate(value) {
      this.showDatepicker(false)
      if (value.date) {
        if (this.isSameOrBefore(value.date, this.datepickerBirthLimit.max)) {
          this.form.birth_date = this.parseDate(value.date)
        }
      }
    },

    resetForm(clear = false) {
      if (this.user) {
        this.form.full_name = clear ? null : this.user.full_name || null
        this.form.birth_date = clear
          ? null
          : (this.user.birth_date
              ? this.parseDate(this.user.birth_date)
              : null) || null
        this.form.address_1 = clear ? null : this.user.address_1 || null
        this.form.city = clear ? null : this.user.city || null
        this.form.postal_code = clear ? null : this.user.postal_code || null
        this.form.state = clear ? null : this.user.state || null
        this.form.country = clear ? null : this.user.country || null
      }
      this.scrollToTop()
    },

    async onSubmit() {
      if (this.form.$busy) return
      this.form.$setLoading(true)
      this.addAlert({
        state: true,
        label: 'Saving...',
        countdown: true
      })
      await this.updateUser(this.form.$data())
        .then(() => {
          this.afterSubmit(false, true, 'Your profile has been updated!')
        })
        .catch(error => {
          this.form.$handleError(error)
          this.afterSubmit(false, false, this.form.$errorMessage)
        })
    },

    /**
     * File uploads
     * ==================================================================================
     */
    onAvatarChange(e) {
      this.showAvatarUpload = true
      this.$refs.avatarCropper.onFileChange(e)
    },

    async uploadAvatar(image) {
      this.form.$setLoading(true)
      let data = new FormData()
      data.append('avatar', image)
      await this.changeAvatar(data)
        .then(() => {
          this.afterSubmit(
            false,
            true,
            'Your profile photo has been updated!',
            false
          )
          this.$root.$emit('uploadComplete')
        })
        .catch(error => {
          this.form.$handleError(error)
          this.afterSubmit(false, false, this.form.$errorMessage, false)
        })
    },

    onPhotoFrontChange(e) {
      this.showPhotoFrontUpload = true
      this.$refs.photoFrontCropper.onFileChange(e)
    },

    async uploadPhotoFront(image) {
      this.form.$setLoading(true)
      let data = new FormData()
      data.append('photo_id_front', image)
      await this.changePhoto(data)
        .then(() => {
          this.afterSubmit(
            false,
            true,
            'Your ID photo has been updated!',
            false
          )
          this.$root.$emit('uploadComplete')
        })
        .catch(error => {
          this.form.$handleError(error)
          this.afterSubmit(false, false, this.form.$errorMessage, false)
        })
    },

    onHeadshotChange(e) {
      this.showHeadshotUpload = true
      this.$refs.headshotCropper.onFileChange(e)
    },

    async uploadHeadshot(image) {
      this.form.$setLoading(true)
      let data = new FormData()
      data.append('headshot', image)
      await this.changePhoto(data)
        .then(() => {
          this.afterSubmit(
            false,
            true,
            'Your headshot photo has been updated!',
            false
          )
          this.$root.$emit('uploadComplete')
        })
        .catch(error => {
          this.form.$handleError(error)
          this.afterSubmit(false, false, this.form.$errorMessage, false)
        })
    },

    afterSubmit(loading, state, message, scroll = true) {
      this.form.$setLoading(loading)
      this.addAlert({
        state: state,
        label: message,
        countdown: 3
      })

      if (scroll) {
        this.scrollToTop()
      }
    }
  },
  created() {
    this.resetForm()
  }
}
</script>
<style scoped lang="scss">
.profile-form /deep/ {
  .divider {
    border-bottom: 1px solid var(--border-color);
  }

  .account-alert {
    a {
      font-weight: initial;
      color: var(--brand-warning);
    }

    a:hover,
    a:focus {
      text-decoration: none;
    }
  }
}

@media (max-width: 768px) {
  .profile-form /deep/ {
    .divider {
      display: none;
    }
  }
}
</style>
